// src/components/services/FixedDeposits.jsx
import React from "react";
import { motion } from "framer-motion";
import { Link } from 'react-router-dom'; // Import Link
import {
    MdOutlineAccountBalance,
    MdOutlineDescription,
    MdOutlineVerifiedUser,
    MdOutlineSecurity,
    MdPayments,
} from "react-icons/md";
import {
    FaPhoneAlt,
    FaPiggyBank,
    FaPercent,
    FaCalendarAlt,
    FaHandHoldingUsd,
} from "react-icons/fa";


function FixedDeposits() {
    const serviceName = "Fixed Deposits"; // Set service name

    // Animation Variants (Keep or move to utils/animations.js)
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2, // Stagger animation of child elements
            },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 },
    };
    return (
        <section className="py-16 md:py-24 px-4 md:px-8 bg-gray-50 text-gray-700">
            <div className="container mx-auto">
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={containerVariants}
                >
                    <motion.h1
                        variants={itemVariants}
                        className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 md:mb-8 text-center"
                    >
                        Fixed Deposits
                    </motion.h1>
                    <motion.p
                        variants={itemVariants}
                        className="text-lg md:text-xl text-gray-600 mb-10 md:mb-12 text-center max-w-3xl mx-auto"
                    >
                        Grow your savings safely and steadily with Fixed Deposits from Keziah
                        Global Private Limited, in partnership with Fino Payments Bank. Enjoy
                        attractive interest rates, flexible tenures, and the security of
                        investing with a trusted name.
                    </motion.p>

                    {/* Features and Benefits */}
                    <motion.div
                        variants={itemVariants}
                        className="mb-12 md:mb-16 bg-white rounded-xl shadow-md p-6 md:p-10"
                    >
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                            Key Features
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                            {/* Feature 1 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <FaPercent className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Attractive Interest Rates
                                    </h4>
                                    <p className="text-gray-600">
                                        Earn higher interest rates on your savings compared to regular savings accounts.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 2 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.1 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <FaCalendarAlt className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Flexible Tenures
                                    </h4>
                                    <p className="text-gray-600">
                                        Choose a tenure that suits your financial goals, ranging from a few months to several years.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 3 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.2 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <FaHandHoldingUsd className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Loan Against FD
                                    </h4>
                                    <p className="text-gray-600">
                                        Avail a loan against your FD in times of need, without breaking the deposit.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 4 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.3 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdOutlineSecurity className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Safe and Secure
                                    </h4>
                                    <p className="text-gray-600">
                                        Your investment is safe with us, backed by the trust and reliability of Fino Payments Bank.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 5 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.4 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdOutlineVerifiedUser className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                       Simple and Easy Process
                                    </h4>
                                    <p className="text-gray-600">
                                        Open an FD account quickly and conveniently with minimal documentation.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 6 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.5 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdPayments className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                       Premature Withdrawal
                                    </h4>
                                    <p className="text-gray-600">
                                        Option for premature withdrawal available, subject to applicable terms and conditions.
                                    </p>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>

                    {/* Why Choose Fixed Deposits with Keziah */}
                    <motion.div
                        variants={itemVariants}
                        className="mb-12 md:mb-16 bg-white rounded-xl shadow-md p-6 md:p-10"
                    >
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                            Why Choose Fixed Deposits with Keziah
                        </h3>
                        <ul className="list-disc pl-8 space-y-4 text-gray-600 text-lg">
                        <li>Partnership with Fino Payments Bank, a trusted name in the financial industry.</li>
                        <li>Competitive interest rates to maximize your returns.</li>
                        <li>Personalized service and support from our team in Salem.</li>
                        <li>Easy account opening process with minimal documentation.</li>
                        <li>Secure and reliable investment option.</li>
                        </ul>
                    </motion.div>

                    {/* Call to Action */}
                    <motion.div
                        variants={itemVariants}
                        className="mb-12 md:mb-16 bg-white rounded-xl shadow-md p-6 md:p-10"
                    >
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                            Start Growing Your Savings with a Fixed Deposit Today!
                        </h3>
                        <p className="text-gray-600 text-lg text-center">
                        Visit our branch in Salem, Tamil Nadu, to open a Fixed Deposit account and start earning attractive returns on your savings. You can also request a callback to learn more about our FD options.
                        </p>
                        <motion.div
                            variants={itemVariants}
                            className="mt-10 flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-6"
                        >
                            <a
                                href="tel:9043588446"
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-full text-base md:text-lg transition duration-300 ease-in-out shadow-md hover:shadow-lg text-center w-full md:w-auto flex items-center justify-center"
                                whileHover={{ scale: 1.05 }}
                            >
                                <FaPhoneAlt className="inline-block mr-2" />
                                Call Now
                            </a>
                            {/* Use Link for Callback */}
                            <Link to={`/callback?serviceName=${encodeURIComponent(serviceName)}`}>
                            <motion.button
                                    className="bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-full text-base md:text-lg transition duration-300 ease-in-out shadow-md hover:shadow-lg text-center w-full md:w-auto flex items-center justify-center"
                                    whileHover={{ scale: 1.05 }}
                                >
                                    Request a Callback
                                </motion.button>
                            </Link>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}

export default FixedDeposits;